import type { Celda, Columna, DocumentoExportable, Seccion } from "@/componentes/exportar/modelo"

import { antiguedad, type Partida, TRAMOS } from "./indicadoresCartera"

/**
 * La cartera —por cobrar o por pagar— lista para «Exportar» (29/10).
 *
 * ⚠ **Se arma con los datos y no leyendo la tabla.** En `TablaSaldos` el concepto es un
 * botón que abre la ficha, y el lector de tablas salta los botones: el Excel saldría
 * sin concepto. Y la cifra lleva su moneda pegada («USD 1.200,00» junto a «BS …»), que
 * en una columna mezclada se queda en texto.
 *
 * ⚠ **La moneda va en su columna y el monto como número.** La antigüedad, igual que la
 * tabla de la pantalla, por moneda y sin convertir: una columna de saldo por moneda.
 */

export type FilaCartera = {
  tercero: string
  concepto: string
  /** El expediente o la clave del 2.0: lo que permite encontrar la cuenta. */
  referencia: string | null
  emision?: string | null
  vence: string | null
  estado: string
  /** Cómo está en el 2.0, si es una cuenta espejo. */
  estado2: string | null
  moneda: string
  monto: string
  aplicado: string
}

const centimos = (texto: string) => Math.round(Number(texto) * 100)

function seccionAntiguedad(
  titulo: string,
  partidas: readonly Partida[],
  hoy: string,
): Seccion | null {
  const tramos = antiguedad(partidas, hoy)
  const visibles = TRAMOS.filter((t) => tramos[t.clave].cuentas > 0)
  if (visibles.length === 0) return null
  const monedas = [
    ...new Set(visibles.flatMap((t) => tramos[t.clave].montos.map((m) => m.divisa))),
  ].sort()
  return {
    titulo,
    columnas: [
      { titulo: "Tramo", tipo: "texto" },
      { titulo: "Cuentas", tipo: "numero" },
      ...monedas.map((m): Columna => ({ titulo: `Saldo (${m})`, tipo: "monto" })),
    ],
    filas: visibles.map((t) => {
      const b = tramos[t.clave]
      return {
        celdas: [
          t.etiqueta,
          b.cuentas,
          ...monedas.map((m): Celda => {
            const cifra = b.montos.find((x) => x.divisa === m)
            return cifra ? Number(cifra.monto) : null
          }),
        ],
      }
    }),
  }
}

export function documentoDeCartera({
  titulo,
  subtitulo,
  etiquetaTercero,
  etiquetaReferencia,
  etiquetaAplicado,
  conEmision,
  filas,
  antiguedadDe,
}: {
  titulo: string
  subtitulo: string
  etiquetaTercero: string
  etiquetaReferencia: string
  etiquetaAplicado: string
  conEmision: boolean
  filas: readonly FilaCartera[]
  /** Toda la cartera, como la tabla de antigüedad de la pantalla (no sólo lo filtrado). */
  antiguedadDe: { titulo: string; partidas: readonly Partida[]; hoy: string }
}): DocumentoExportable {
  const columnas: Columna[] = [
    { titulo: etiquetaTercero, tipo: "texto" },
    { titulo: "Concepto", tipo: "texto" },
    { titulo: etiquetaReferencia, tipo: "texto" },
    ...(conEmision ? [{ titulo: "Emisión", tipo: "fecha" } as Columna] : []),
    { titulo: "Vencimiento", tipo: "fecha" },
    { titulo: "Estado", tipo: "texto" },
    { titulo: "En el 2.0", tipo: "texto" },
    { titulo: "Moneda", tipo: "texto" },
    { titulo: "Monto", tipo: "monto" },
    { titulo: etiquetaAplicado, tipo: "monto" },
    { titulo: "Saldo", tipo: "monto" },
  ]
  const cartera: Seccion = {
    titulo,
    columnas,
    filas: filas.map((f) => ({
      celdas: [
        f.tercero,
        f.concepto,
        f.referencia,
        ...(conEmision ? [f.emision?.slice(0, 10) ?? null] : []),
        f.vence?.slice(0, 10) ?? null,
        f.estado,
        f.estado2,
        f.moneda,
        Number(f.monto),
        Number(f.aplicado),
        (centimos(f.monto) - centimos(f.aplicado)) / 100,
      ],
    })),
  }
  const tramos = seccionAntiguedad(antiguedadDe.titulo, antiguedadDe.partidas, antiguedadDe.hoy)
  return { titulo, subtitulo, secciones: tramos ? [cartera, tramos] : [cartera] }
}
